import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { radioClubs, type RfwClub } from '@/mocks/rfwClubs';

function formatNumber(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

interface RadioClubSectionProps {
  stationName: string;
}

export default function RadioClubSection({ stationName }: RadioClubSectionProps) {
  const club = useMemo(() => {
    return radioClubs.find(c =>
      c.parentName.toLowerCase() === stationName.toLowerCase()
    );
  }, [stationName]);

  if (!club) {
    return (
      <section className="bg-background-50 rounded-2xl p-5 md:p-6 border border-background-200/70">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
            <i className="ri-group-line text-base text-accent-600"></i>
          </div>
          <h2 className="text-lg font-extrabold text-foreground-950">Club Oficial</h2>
        </div>
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
            <i className="ri-building-4-line text-2xl text-foreground-300"></i>
          </div>
          <p className="text-sm text-foreground-500 mb-4">Esta radio aún no tiene su Club Oficial RFW.</p>
          <button className="px-5 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-all">
            <i className="ri-add-line mr-1.5"></i>
            Crear Club Oficial
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-background-50 rounded-2xl p-5 md:p-6 border border-background-200/70">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
            <i className="ri-group-fill text-xl text-accent-600"></i>
          </div>
          <div>
            <h2 className="text-lg font-extrabold text-foreground-950 flex items-center gap-2">
              {club.clubName}
              <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-bold">
                Club Oficial
              </span>
            </h2>
            <p className="text-xs text-foreground-400 italic">{club.slogan}</p>
          </div>
        </div>
        <Link
          to="/clubes"
          className="text-xs text-accent-600 hover:text-accent-700 font-medium cursor-pointer whitespace-nowrap flex items-center gap-1"
        >
          Ver todos los clubes
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>

      {/* Description */}
      <p className="text-sm text-foreground-500 leading-relaxed mb-5">{club.description}</p>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="bg-background-100 rounded-xl p-3 text-center">
          <span className="block text-lg font-extrabold text-foreground-950">{formatNumber(club.membersCount)}</span>
          <span className="text-[10px] text-foreground-400">Miembros</span>
        </div>
        <div className="bg-background-100 rounded-xl p-3 text-center">
          <span className="block text-lg font-extrabold text-foreground-950">{formatNumber(club.postsCount)}</span>
          <span className="text-[10px] text-foreground-400">Publicaciones</span>
        </div>
        <div className="bg-background-100 rounded-xl p-3 text-center">
          <span className="block text-lg font-extrabold text-foreground-950">{club.activeChallengeCount}</span>
          <span className="text-[10px] text-foreground-400">Retos Activos</span>
        </div>
        <div className="bg-background-100 rounded-xl p-3 text-center">
          <span className="block text-lg font-extrabold text-foreground-950">{club.eventsCount}</span>
          <span className="text-[10px] text-foreground-400">Eventos</span>
        </div>
      </div>

      {/* Club Score + Engagement */}
      <div className="bg-background-100 rounded-xl p-4 mb-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-foreground-700 flex items-center gap-1.5">
            <i className="ri-flashlight-fill text-accent-500"></i>
            RFW Club Score
          </span>
          <div className="flex items-center gap-3">
            <span className="text-sm font-extrabold text-foreground-950">{club.clubScore.toLocaleString()} pts</span>
            <span className={`text-[11px] font-semibold ${club.scoreChange > 0 ? 'text-emerald-600' : club.scoreChange < 0 ? 'text-red-500' : 'text-foreground-400'}`}>
              {club.scoreChange > 0 ? '+' : ''}{club.scoreChange} semana
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-foreground-400">
          <span>Ranking #{club.rankingPosition} en RFW</span>
          <span>·</span>
          <span>Engagement {club.engagementRate}%</span>
          <span>·</span>
          <span>Fundado {new Date(club.foundedDate).toLocaleDateString('es-MX', { year: 'numeric', month: 'short' })}</span>
        </div>
      </div>

      {/* Active Challenges */}
      {club.challenges.filter(c => c.active).length > 0 && (
        <div className="mb-5">
          <h4 className="text-sm font-extrabold text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-sword-line text-orange-500"></i>
            Retos Activos del Club
          </h4>
          <div className="space-y-2">
            {club.challenges.filter(c => c.active).slice(0, 2).map((ch) => (
              <div key={ch.id} className="bg-background-100 rounded-xl p-3 flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center shrink-0">
                  <i className="ri-sword-line text-orange-600 text-sm"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="text-sm font-bold text-foreground-950">{ch.title}</span>
                    <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full whitespace-nowrap shrink-0">{ch.reward}</span>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1.5">{ch.description}</p>
                  <div className="flex items-center gap-3 text-[10px] text-foreground-400">
                    <span>{ch.participants} participantes</span>
                    <span>Finaliza: {new Date(ch.endDate).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Benefits */}
      <div className="mb-5">
        <h4 className="text-sm font-extrabold text-foreground-950 mb-3 flex items-center gap-2">
          <i className="ri-gift-line text-accent-500"></i>
          Beneficios del Club
        </h4>
        <div className="flex flex-wrap gap-2">
          {[
            { enabled: club.hasBadges, icon: 'ri-award-line', label: 'Insignias exclusivas', cls: 'bg-violet-50 text-violet-600' },
            { enabled: club.hasEarlyAccess, icon: 'ri-timer-flash-line', label: 'Acceso anticipado', cls: 'bg-rose-50 text-rose-600' },
            { enabled: club.hasGiveaways, icon: 'ri-gift-line', label: 'Sorteos para miembros', cls: 'bg-emerald-50 text-emerald-600' },
            { enabled: club.hasRecognition, icon: 'ri-star-line', label: 'Reconocimiento en vivo', cls: 'bg-amber-50 text-amber-600' },
          ].map((b) => (
            <span key={b.label} className={`inline-flex items-center gap-1 px-3 py-2 rounded-xl text-[11px] font-semibold ${b.enabled ? b.cls : 'bg-background-200 text-foreground-300'}`}>
              <i className={`${b.icon} text-sm`}></i>
              {b.label}
            </span>
          ))}
        </div>
      </div>

      {/* Top Members preview */}
      {club.topMembers.length > 0 && (
        <div className="mb-5">
          <h4 className="text-sm font-extrabold text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-user-star-line text-accent-500"></i>
            Miembros Destacados
          </h4>
          <div className="flex flex-wrap gap-3">
            {club.topMembers.map((m) => (
              <div key={m.id} className="flex items-center gap-2 bg-background-100 rounded-xl px-3 py-2">
                <img src={m.avatarUrl} alt={m.name} className="w-7 h-7 rounded-full object-cover" loading="lazy" />
                <div>
                  <p className="text-[11px] font-bold text-foreground-950">{m.name}</p>
                  <p className="text-[9px] text-foreground-400">{formatNumber(m.score)} pts</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA Button */}
      <Link
        to="/clubes"
        className="w-full flex items-center justify-center gap-2 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-semibold rounded-xl cursor-pointer transition-all"
      >
        <i className="ri-group-line"></i>
        Unirse al Club Oficial
      </Link>
    </section>
  );
}